import type { ComputedRef, Ref } from 'vue';
import {
  computed,
} from 'vue';

import type { CommissionDeal } from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionDeals/types';
import type {
  IAffiliateProgramCommissionFilterFormWithErrors,
  IAffiliateProgramCommissionFilterItemAttributePayload,
  ICommissionCountryFilterItem,
  ICommissionsConstantsResponse,
  IConditionVersions,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type { IFormSelectInputOption } from '@modules/forms/types';

export interface CommissionDealsComposable {
  deals: Ref<CommissionDeal[]>;
  dealsResultOptions: Ref<IFormSelectInputOption<string>[]>;
  dealsComparisonOptions: Ref<IFormSelectInputOption<string>[]>;
  onDealAdd(values: IAffiliateProgramCommissionFilterItemAttributePayload): void;
  onDealRemove(index: number): void;
  onDealChange(index: number, values: IAffiliateProgramCommissionFilterItemAttributePayload): void;
}

export default function useCommissionDeals(
  activeCountryFilter: Ref<IAffiliateProgramCommissionFilterFormWithErrors | undefined>,
  transactionType: Ref<string | undefined>,
  countryFilters: Ref<ICommissionCountryFilterItem[]>,
  activeFilterIndex: Ref<number>,
  constants: Ref<ICommissionsConstantsResponse | undefined>,
  currentFirstGroupVersion: ComputedRef<IConditionVersions | undefined>,
): CommissionDealsComposable {
  const filterDeals = computed<IAffiliateProgramCommissionFilterItemAttributePayload[]>(() => (
    activeCountryFilter.value?.condition_items_attributes ?? []
  ));

  const deals = computed<CommissionDeal[]>(() => {
    const buttons: CommissionDeal[] = [];

    filterDeals.value.forEach((item, index) => {
      if (!item._destroy) {
        buttons.push({
          index,
          value: item,
        });
      }
    });

    return buttons;
  });

  const dealsResultOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value || !transactionType.value || !['cpa', 'hybrid'].includes(transactionType.value) || !currentFirstGroupVersion.value) {
      return [];
    }

    const options = transactionType.value === 'hybrid'
      ? constants.value.constants.condition_items.obtained_result.hybrid.cpa[currentFirstGroupVersion.value]
      : constants.value.constants.condition_items.obtained_result.cpa[currentFirstGroupVersion.value];

    return Object.entries(options ?? {})
      .map(([label, value]) => ({
        label,
        value,
      }));
  });

  const dealsComparisonOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    return Object.entries(constants.value.constants.condition_items.condition_for_comparison)
      .map(([value, label]) => ({
        label,
        value,
      }));
  });

  function onDealAdd(values: IAffiliateProgramCommissionFilterItemAttributePayload): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      countryFilters.value[activeFilterIndex.value] = {
        ...activeFilter,
        condition_items_attributes: [
          ...activeFilter.condition_items_attributes,
          values,
        ],
      };
    }
  }

  function onDealRemove(index: number): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      const attributes = [...activeFilter.condition_items_attributes];
      const attribute = attributes[index];

      if (attribute) {
        if (attribute.id) {
          attributes.splice(index, 1, {
            ...attribute,
            _destroy: 1,
          });
        } else {
          attributes.splice(index, 1);
        }

        countryFilters.value[activeFilterIndex.value] = {
          ...activeFilter,
          condition_items_attributes: attributes,
        };
      }
    }
  }

  function onDealChange(index: number, values: IAffiliateProgramCommissionFilterItemAttributePayload): void {
    const activeFilter = countryFilters.value[activeFilterIndex.value];
    if (activeFilter) {
      const attributes = [...activeFilter.condition_items_attributes];
      const attribute = attributes[index];

      if (attribute) {
        attributes.splice(index, 1, {
          ...attribute,
          ...values,
          desired_value: values.condition_for_comparison === 'any' ? undefined : values.desired_value,
        });

        countryFilters.value[activeFilterIndex.value] = {
          ...activeFilter,
          condition_items_attributes: attributes,
        };
      }
    }
  }

  return {
    deals,
    dealsResultOptions,
    dealsComparisonOptions,
    onDealAdd,
    onDealRemove,
    onDealChange,
  };
}
